import { useCallback, useMemo } from 'react';

import {
  VisibilitySettingsAccessTypeEnum,
  VisibilitySettingsRestrictionType,
  VisibilitySettingsRestrictionTypeEnum,
} from '@/types/schema';

import { ContextualGroupsConfigType, VisibilitySettingsValueType } from '../types';
import { parseVisibilitySettingsRestrictionConfig } from '../utils';

const useVisibilitySettingsActions = (
  props: Pick<VisibilitySettingsValueType, 'value' | 'onChange'> & {
    contextualGroupsConfig?: ContextualGroupsConfigType;
  },
) => {
  const { value, onChange, contextualGroupsConfig } = props;

  const { groupIds, contextualGroups, userIds } = useMemo(
    () => parseVisibilitySettingsRestrictionConfig(value),
    [value],
  );

  const onAccessTypeChange = useCallback(
    (nextAccessType: string) => {
      const accessType = nextAccessType as VisibilitySettingsAccessTypeEnum;

      if (accessType === VisibilitySettingsAccessTypeEnum.Open) {
        onChange({ accessType });
      } else {
        onChange({ accessType, restrictionConfigs: value.restrictionConfigs });
      }
    },
    [onChange, value.restrictionConfigs],
  );

  const onRestrictionConfigChange = useCallback(
    ({
      nextGroupIds,
      nextContextualGroupIds,
      nextUserIds,
    }: {
      nextGroupIds: string[];
      nextContextualGroupIds?: VisibilitySettingsRestrictionTypeEnum[];
      nextUserIds?: string[];
    }) => {
      const restrictionConfigs = [] as VisibilitySettingsRestrictionType[];

      if (nextGroupIds?.length) {
        restrictionConfigs.push({
          type: VisibilitySettingsRestrictionTypeEnum.Groups,
          entityIds: nextGroupIds,
        });
      }

      if (nextUserIds?.length) {
        restrictionConfigs.push({
          type: VisibilitySettingsRestrictionTypeEnum.Users,
          entityIds: nextUserIds,
        });
      }

      if (nextContextualGroupIds?.length) {
        nextContextualGroupIds.forEach((nextContextualGroupId) =>
          restrictionConfigs.push({
            type: nextContextualGroupId,
            contextualUserId: contextualGroupsConfig?.contextualUserId,
          }),
        );
      }

      onChange({ accessType: value.accessType, restrictionConfigs });
    },
    [contextualGroupsConfig?.contextualUserId, onChange, value.accessType],
  );

  const onAddUser = useCallback(
    ({ userId }: { userId?: string }) => {
      onRestrictionConfigChange({
        nextGroupIds: groupIds,
        nextContextualGroupIds: contextualGroups,
        nextUserIds: userId ? [...userIds, userId] : userIds,
      });
    },
    [contextualGroups, groupIds, onRestrictionConfigChange, userIds],
  );

  const onAddGroup = useCallback(
    ({
      contextualGroup,
      groupId,
    }: {
      groupId?: string;
      contextualGroup?: VisibilitySettingsRestrictionTypeEnum;
    }) => {
      onRestrictionConfigChange({
        nextGroupIds: groupId ? [...groupIds, groupId] : groupIds,
        nextContextualGroupIds: contextualGroup
          ? [...contextualGroups, contextualGroup]
          : contextualGroups,
        nextUserIds: userIds,
      });
    },
    [contextualGroups, groupIds, onRestrictionConfigChange, userIds],
  );

  const onAddMultipleGroups = useCallback(
    ({
      contextualGroup,
      newGroupIds,
    }: {
      newGroupIds?: string[];
      contextualGroup?: VisibilitySettingsRestrictionTypeEnum;
    }) => {
      onRestrictionConfigChange({
        nextGroupIds: newGroupIds?.length ? [...groupIds, ...newGroupIds] : groupIds,
        nextContextualGroupIds: contextualGroup
          ? [...contextualGroups, contextualGroup]
          : contextualGroups,
        nextUserIds: userIds,
      });
    },
    [contextualGroups, groupIds, onRestrictionConfigChange, userIds],
  );

  const onRemoveUser = useCallback(
    ({ removedUserId }: { removedUserId?: string }) => {
      onRestrictionConfigChange({
        nextGroupIds: groupIds,
        nextContextualGroupIds: contextualGroups,
        nextUserIds: userIds.filter((id) => id !== removedUserId),
      });
    },
    [contextualGroups, groupIds, onRestrictionConfigChange, userIds],
  );

  const onRemoveGroup = useCallback(
    ({
      removedGroupId,
      removedContextualGroupId,
    }: {
      removedGroupId?: string;
      removedContextualGroupId?: VisibilitySettingsRestrictionTypeEnum;
    }) => {
      onRestrictionConfigChange({
        nextGroupIds: groupIds.filter((groupId) => groupId !== removedGroupId),
        nextContextualGroupIds: contextualGroups.filter((id) => id !== removedContextualGroupId),
        nextUserIds: userIds,
      });
    },
    [contextualGroups, groupIds, onRestrictionConfigChange, userIds],
  );

  return {
    onAccessTypeChange,
    onRestrictionConfigChange,
    onRemoveUser,
    onRemoveGroup,
    onAddGroup,
    onAddMultipleGroups,
    onAddUser,
  };
};

export default useVisibilitySettingsActions;
